import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { Settings } from "../../../settings";

/**
 * Controls tension spine animations for individual reels
 */
class TensionReelController {
  constructor(reelContainer, reelIndex, symbolWidth, symbolHeight) {
    this.reelContainer = reelContainer;
    this.tensionSpineData = {
      skeleton: "tension-skeleton",
      atlas: "tension-atlas",
    };
    this.reelIndex = reelIndex;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    this.tensionSpine = null;
    this.isVisible = false;
    this.initialized = false;

    // Spine scaling configuration
    this.heightMultiplier = 1.0432; // Default size, no scaling
    this.yOffsetMultiplier = 0.78; // No offset

    // Development flag to keep tension always visible
    this.DEV_ALWAYS_VISIBLE = false;

    // Track active timeouts for cleanup
    this.currentTimeouts = [];

    this.initializeTensionSpine();
  }

  /**
   * Calculate responsive scale and positioning for spine animation
   * @param {Spine} spineInstance - The spine instance to scale
   * @param {number} containerWidth - Available container width
   * @param {number} containerHeight - Available container height
   * @returns {Object} - Scale and positioning information
   */
  calculateSpineScale(spineInstance, containerWidth, containerHeight) {
    // Set default animation to get proper bounds
    if (
      spineInstance &&
      spineInstance.state &&
      spineInstance.state.data &&
      spineInstance.state.data.skeletonData.animations.length > 0
    ) {
      const defaultAnimation =
        spineInstance.state.data.skeletonData.animations[0];
      spineInstance.state.setAnimation(0, defaultAnimation.name, true);
    }

    // Calculate scale to fit within container while maintaining aspect ratio
    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;

    const widthScale = containerWidth / nativeWidth;
    const heightScale = containerHeight / nativeHeight;
    const scale = Math.min(widthScale, heightScale);

    // Calculate final dimensions and centering offsets
    const scaledWidth = nativeWidth * scale;
    const scaledHeight = nativeHeight * scale;
    const x = (containerWidth - scaledWidth) / 2;
    const y = (containerHeight - scaledHeight) / 2;

    return {
      scale,
      nativeWidth,
      nativeHeight,
      x,
      y,
      scaledWidth,
      scaledHeight,
    };
  }

  /**
   * Hide tension animation immediately
   */
  hideTension() {
    if (!this.tensionSpine || this.tensionSpine.destroyed) {
      this.isVisible = false;
      return;
    }

    requestAnimationFrame(() => {
      if (!this.tensionSpine || this.tensionSpine.destroyed) {
        this.isVisible = false;
        return;
      }

      this.tensionSpine.alpha = 0.001; // Keep in render pipeline (GPU buffers stay hot)
      this.isVisible = false;

      if (this.tensionSpine.state) {
        try {
          this.tensionSpine.state.clearTracks();
        } catch (error) {
          console.warn(
            `[TensionReelController] Clear tracks error for reel ${this.reelIndex}:`,
            error
          );
        }
      }
    });
  }

  /**
   * Force stop tension animations with smooth exit transition
   */
  forceStopTension() {
    this.clearAllTimeouts();

    if (
      !this.tensionSpine ||
      this.tensionSpine.destroyed ||
      !this.tensionSpine.state ||
      !this.getAvailableAnimations().includes("tension_out")
    ) {
      this.hideTension();
      return;
    }

    // Check if currently playing tension animations
    const currentAnimation = this.tensionSpine.state.getCurrent(0);

    // If tension_out is already playing, don't restart it
    if (currentAnimation && currentAnimation.animation.name === "tension_out") {
      return; // Already transitioning out, let it finish
    }

    const isPlayingTension =
      currentAnimation &&
      (currentAnimation.animation.name === "tension_in" ||
        currentAnimation.animation.name === "tension_idle");

    if (isPlayingTension) {
      // Switch to tension_out and play to completion
      this.tensionSpine.state.setAnimation(0, "tension_out", false);

      // Schedule hide based on animation duration
      const animationDuration =
        this.tensionSpine.skeleton.data.animations.find(
          (anim) => anim.name === "tension_out"
        )?.duration || 1.0;

      const timeoutId = setTimeout(() => {
        this.hideTension();
      }, animationDuration * 1000);

      this.currentTimeouts.push(timeoutId);
    } else {
      this.hideTension();
    }
  }

  /**
   * Clear all active timeouts
   */
  clearAllTimeouts() {
    this.currentTimeouts.forEach(clearTimeout);
    this.currentTimeouts = [];
  }

  /**
   * Play tension intro animation and transition to idle loop
   */
  playTensionIn() {
    if (
      !this.tensionSpine ||
      this.tensionSpine.destroyed ||
      !this.tensionSpine.state
    ) {
      return;
    }

    requestAnimationFrame(() => {
      if (
        !this.tensionSpine ||
        this.tensionSpine.destroyed ||
        !this.tensionSpine.state
      ) {
        return;
      }

      try {
        this.tensionSpine.state.clearTracks();
        this.tensionSpine.state.clearListeners();
        this.tensionSpine.state.apply(this.tensionSpine.skeleton);
        this.tensionSpine.skeleton.setToSetupPose();

        this.tensionSpine.alpha = 1; // Show (use alpha not visible)
        this.isVisible = true;

        const animations = this.getAvailableAnimations();

        if (
          animations.includes("tension_in") &&
          animations.includes("tension_idle")
        ) {
          this.tensionSpine.state.setAnimation(0, "tension_in", false);
          this.tensionSpine.state.addAnimation(0, "tension_idle", true, 0);
        } else if (animations.includes("tension_idle")) {
          this.tensionSpine.state.setAnimation(0, "tension_idle", true);
        } else if (animations.length > 0) {
          this.tensionSpine.state.setAnimation(0, animations[0], true);
        }
      } catch (error) {
        console.warn(
          `[TensionReelController] Tension in animation error for reel ${this.reelIndex}:`,
          error
        );
        this.hideTension();
      }
    });
  }

  /**
   * Play tension exit animation
   */
  playTensionOut() {
    if (
      !this.tensionSpine ||
      this.tensionSpine.destroyed ||
      !this.tensionSpine.state
    ) {
      return;
    }

    // Check if tension_out is already playing - don't restart it
    const currentAnimation = this.tensionSpine.state.getCurrent(0);
    if (currentAnimation && currentAnimation.animation.name === "tension_out") {
      return; // Already playing tension_out, let it finish
    }

    this.clearAllTimeouts();

    requestAnimationFrame(() => {
      if (
        !this.tensionSpine ||
        this.tensionSpine.destroyed ||
        !this.tensionSpine.state
      ) {
        return;
      }

      try {
        this.tensionSpine.state.clearTracks();
        this.tensionSpine.state.clearListeners();
        this.tensionSpine.state.apply(this.tensionSpine.skeleton);
        this.tensionSpine.skeleton.setToSetupPose();

        const animations = this.getAvailableAnimations();

        let animationToPlay = null;

        if (animations.includes("tension_out")) {
          animationToPlay = "tension_out";
        } else if (animations.length > 0) {
          animationToPlay = animations[0];
        }

        if (animationToPlay) {
          this.tensionSpine.state.setAnimation(0, animationToPlay, false);

          // Get actual animation duration from spine instead of hardcoded timeout
          const animationDuration =
            this.tensionSpine.skeleton.data.animations.find(
              (anim) => anim.name === animationToPlay
            )?.duration || 1.0;

          const timeoutId = setTimeout(() => {
            this.hideTension();
          }, animationDuration * 1000);
          this.currentTimeouts.push(timeoutId);
        } else {
          this.hideTension();
        }
      } catch (error) {
        console.warn(
          `[TensionReelController] Tension out animation error for reel ${this.reelIndex}:`,
          error
        );
        this.hideTension();
      }
    });
  }

  /**
   * Update tension spine dimensions for responsive scaling
   * @param {number} newsymbolWidth - New symbol width
   * @param {number} newsymbolHeight - New symbol height
   */
  resizeTension(newsymbolWidth, newsymbolHeight) {
    if (
      newsymbolWidth === this.symbolWidth &&
      newsymbolHeight === this.symbolHeight
    ) {
      return;
    }

    this.symbolWidth = newsymbolWidth;
    this.symbolHeight = newsymbolHeight;

    if (!this.tensionSpine || this.tensionSpine.destroyed) {
      return;
    }

    requestAnimationFrame(() => {
      if (!this.tensionSpine || this.tensionSpine.destroyed) {
        return;
      }

      try {
        this.applyTensionScaling();
      } catch (error) {
        console.warn(
          `[TensionReelController] Scaling error for reel ${this.reelIndex}:`,
          error
        );
      }
    });
  }

  /**
   * Apply scaling and positioning to tension spine
   */
  applyTensionScaling() {
    if (!this.tensionSpine || this.tensionSpine.destroyed) {
      return;
    }

    const scaleData = this.calculateSpineScale(
      this.tensionSpine,
      this.symbolWidth,
      this.symbolHeight
    );

    if (this.tensionSpine.scale) {
      this.tensionSpine.scale.set(
        scaleData.scale,
        scaleData.scale * this.heightMultiplier
      );
    }

    if (this.tensionSpine.position) {
      const yOffset = this.symbolHeight * this.yOffsetMultiplier;
      this.tensionSpine.position.set(scaleData.x, scaleData.y + yOffset);
    }
  }

  /**
   * Create tension spine instance
   */
  createTensionSpine() {
    try {
      if (!this.tensionSpineData) {
        return null;
      }

      const assets = PIXI.Assets.cache;
      if (
        !assets.has(this.tensionSpineData.skeleton) ||
        !assets.has(this.tensionSpineData.atlas)
      ) {
        return null;
      }

      const skeletonData = PIXI.Assets.get(this.tensionSpineData.skeleton);
      const atlasData = PIXI.Assets.get(this.tensionSpineData.atlas);

      if (!skeletonData || !atlasData) {
        return null;
      }

      const tensionSpine = Spine.from({
        skeleton: this.tensionSpineData.skeleton,
        atlas: this.tensionSpineData.atlas,
      });

      if (!tensionSpine || !tensionSpine.skeleton || !tensionSpine.state) {
        return null;
      }

      if (
        !tensionSpine.skeleton.data ||
        !tensionSpine.skeleton.data.animations
      ) {
        return null;
      }

      return tensionSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get available animation names
   */
  getAvailableAnimations() {
    if (!this.tensionSpine?.skeleton?.data?.animations) {
      return [];
    }
    return this.tensionSpine.skeleton.data.animations.map((anim) => anim.name);
  }

  /**
   * Get tension_out animation duration in milliseconds
   * @returns {number} Duration in milliseconds
   */
  getTensionOutDuration() {
    if (!this.tensionSpine?.skeleton?.data?.animations) {
      return 1000; // Fallback to 1 second
    }

    const tensionOutAnim = this.tensionSpine.skeleton.data.animations.find(
      (anim) => anim.name === "tension_out"
    );

    return tensionOutAnim ? tensionOutAnim.duration * 1000 : 1000;
  }

  /**
   * Warm up tension spine with "tension_in" animation
   */
  warmUpTensionSpine() {
    if (!this.tensionSpine || !this.tensionSpine.state) {
      return;
    }

    try {
      const animations = this.getAvailableAnimations();
      const inAnimation = animations.find((anim) => anim.name === "tension_in");

      if (inAnimation) {
        // Warm up "tension_in" since that's what plays first
        this.tensionSpine.state.setAnimation(0, "tension_in", false);
        this.tensionSpine.state.update(0.016); // Update one frame
        this.tensionSpine.skeleton.updateWorldTransform(); // Populate hierarchy
        this.tensionSpine.state.clearTracks(); // Stop after warmup to avoid wasting CPU
      }

      // Keep at alpha = 0.001 so PixiJS continues rendering it
      this.tensionSpine.alpha = 0.001;
    } catch (error) {
      // Silent fail - warmup is optional optimization
    }
  }

  /**
   * Initialize tension spine and add to container
   */
  initializeTensionSpine() {
    this.tensionSpine = this.createTensionSpine();

    if (!this.tensionSpine) {
      return;
    }

    // Validate spine components
    const missingComponents = [];
    if (!this.tensionSpine.skeleton) missingComponents.push("skeleton");
    if (!this.tensionSpine.state) missingComponents.push("state");
    if (!this.tensionSpine.skeleton?.data)
      missingComponents.push("skeleton.data");
    if (!this.tensionSpine.skeleton?.slots)
      missingComponents.push("skeleton.slots");
    if (!this.tensionSpine.skeleton?.bones)
      missingComponents.push("skeleton.bones");

    if (missingComponents.length > 0) {
      return;
    }

    this.tensionSpine.position.set(0, 0);
    this.tensionSpine.zIndex = 5;
    this.tensionSpine.alpha = 0.001; // Hidden initially, keep in render pipeline
    this.isVisible = false;

    this.reelContainer.addChild(this.tensionSpine);
    this.applyTensionScaling();

    if (this.tensionSpine.parent !== this.reelContainer) {
      return;
    }

    // Minimal warmup - warm up "tension_in" since that's what plays first
    this.warmUpTensionSpine();

    this.initialized = true;
  }

  /**
   * Clean up spine resources and references
   */
  cleanup() {
    this.clearAllTimeouts();

    if (this.tensionSpine && !this.tensionSpine.destroyed) {
      try {
        if (this.tensionSpine.state) {
          this.tensionSpine.state.clearTracks();
          this.tensionSpine.state.clearListeners();
        }

        if (this.tensionSpine.parent) {
          this.tensionSpine.parent.removeChild(this.tensionSpine);
        }

        this.tensionSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        console.warn(
          `[TensionReelController] Cleanup error for reel ${this.reelIndex}:`,
          error
        );
      }
    }

    this.tensionSpine = null;
    this.reelContainer = null;
    this.tensionSpineData = null;
    this.isVisible = false;
    this.initialized = false;
  }
}

export default TensionReelController;
